import { registerViewType, viewTypeRegistry } from './types.js';
import { tableView } from './table/index.js';
import { formView } from './form/index.js';
import { dashboardView } from './dashboard/index.js';
import { kanbanView, kanbanSettingsFromMonday, kanbanSettingsToMonday, } from './kanban/index.js';
import { calendarView, calendarSettingsFromMonday, calendarSettingsToMonday, } from './calendar/index.js';
import { galleryView, gallerySettingsFromMonday, gallerySettingsToMonday, } from './gallery/index.js';
import { ganttView, ganttSettingsFromMonday, ganttSettingsToMonday, } from './gantt/index.js';
import { timelineView, timelineSettingsFromMonday, timelineSettingsToMonday, } from './timeline/index.js';
import { workloadView, workloadSettingsFromMonday, workloadSettingsToMonday, } from './workload/index.js';
import { parseViewSettingsBlob } from './from-monday.js';
import { PLACEHOLDER_VIEW_TYPES } from './placeholders/index.js';
export * from './types.js';
export * from './table/index.js';
export * from './form/index.js';
export * from './dashboard/index.js';
export * from './kanban/index.js';
export * from './calendar/index.js';
export * from './gallery/index.js';
export * from './gantt/index.js';
export * from './timeline/index.js';
export * from './workload/index.js';
export * from './timeline-axis.js';
export * from './column-selection.js';
export * from './from-monday.js';
export * from './placeholders/index.js';
export * from './personal-order.js';
let registered = false;
/**
 * Every view kind monday offers is registered, built or not (`specs/M4-V1-views.md` §5): the
 * gallery's shape never changes when one lands, an imported view keeps its real
 * `BoardView.type`, and an unbuilt kind renders a *coming soon* screen instead of an error.
 */
export function registerBuiltInViewTypes() {
    if (registered)
        return;
    registered = true;
    registerViewType(tableView);
    registerViewType(formView);
    registerViewType(dashboardView);
    registerViewType(kanbanView);
    registerViewType(calendarView);
    registerViewType(galleryView);
    registerViewType(ganttView);
    registerViewType(timelineView);
    registerViewType(workloadView);
    for (const definition of PLACEHOLDER_VIEW_TYPES)
        registerViewType(definition);
}
registerBuiltInViewTypes();
/** The kinds the add-view gallery lists, in monday's own order (§2.5). */
export function galleryViewTypes(section) {
    return viewTypeRegistry
        .list()
        .filter((definition) => definition.gallery.section === section)
        .sort((a, b) => a.gallery.order - b.gallery.order);
}
/** monday's coarse `create_view(type:)` enum -> our registry key. */
export function viewTypeForKind(kind) {
    switch (kind) {
        case 'TABLE': return 'table';
        case 'FORM': return 'form';
        case 'DASHBOARD': return 'dashboard';
        default: return 'app';
    }
}
/** `BoardView.type` -> our registry key; `undefined` when monday's string is unknown to us. */
export function viewTypeForMondayType(mondayViewType) {
    if (mondayViewType === null)
        return undefined;
    return viewTypeRegistry.list().find((definition) => definition.mondayViewType === mondayViewType)?.id;
}
export { viewTypeRegistry };
/**
 * `viewSettingsFromMonday()` — the importer's and `/v2`'s single door into a view's
 * `settings_str` (`specs/M4-V2-kanban.md` §4).
 *
 * A kind with no reader of its own keeps monday's object verbatim, which is what the
 * placeholders' passthrough schema already promises: nothing is ever dropped, and a kind we
 * build later reads the same blob then.
 */
export function viewSettingsFromMonday(registryId, raw) {
    switch (registryId) {
        case 'kanban': return kanbanSettingsFromMonday(raw);
        case 'calendar': return calendarSettingsFromMonday(raw);
        case 'gallery': return gallerySettingsFromMonday(raw);
        case 'gantt': return ganttSettingsFromMonday(raw);
        case 'timeline': return timelineSettingsFromMonday(raw);
        case 'workload': return workloadSettingsFromMonday(raw);
        default: {
            const { object, warnings } = parseViewSettingsBlob(raw, registryId ?? 'unknown');
            return { settings: object, warnings };
        }
    }
}
/** The inverse, for the exporter and `BoardView.settings_str`. */
export function viewSettingsToMonday(registryId, settings) {
    switch (registryId) {
        case 'kanban': return kanbanSettingsToMonday(settings);
        case 'calendar': return calendarSettingsToMonday(settings);
        case 'gallery': return gallerySettingsToMonday(settings);
        case 'gantt': return ganttSettingsToMonday(settings);
        case 'timeline': return timelineSettingsToMonday(settings);
        case 'workload': return workloadSettingsToMonday(settings);
        default:
            return settings !== null && typeof settings === 'object' && !Array.isArray(settings)
                ? settings
                : {};
    }
}
//# sourceMappingURL=index.js.map